import { BUILDER_FEE } from "#/hooks/builder-fee";

/**
 * Minimum Hyperliquid account value (in USDC) required to claim a referral
 * code. Must mirror the backend's REFERRAL_MIN_ACCOUNT_VALUE_USD — the check
 * itself is server-side; this value is display-only (error messages +
 * on-form warning).
 */
export const REFERRAL_MIN_USD = (() => {
	const raw = import.meta.env.VITE_REFERRAL_MIN_USD;
	const parsed = Number.parseFloat(raw ?? "");
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 5;
})();

/**
 * Block explorer for the chain we pay out on. Used to build tx_hash
 * links in withdrawal history. Hardcoded here because Arbiscan's URL
 * format isn't config; if the payout chain changes (e.g. Base), update
 * alongside the backend's ARB_CHAIN_ID.
 */
export const ARBISCAN_TX_BASE = "https://arbiscan.io/tx/";

export interface ReferralProduct {
	nameKey: string;
	descriptionKey: string;
	taker: number;
	maker: number;
	highlight: boolean;
}

export const BUILDER_FEE_PERCENT = (BUILDER_FEE / 100_000) * 100;

/**
 * Display-only fallback for the login gate while app settings load (or fail):
 * the default revenue share every referrer gets. Must mirror the backend's
 * default — the server value replaces it as soon as settings arrive, and all
 * real payouts are computed server-side.
 */
export const DEFAULT_REVENUE_SHARE_BPS = 5_000;

export function computeRatios(
	referralRate: number,
	products: ReferralProduct[],
) {
	let maxRatio = 0;
	let maxKey = "";

	const rows = products.map((p) => {
		const takerRatio = (referralRate / p.taker) * 100;
		const makerRatio = (referralRate / p.maker) * 100;
		if (takerRatio > maxRatio) {
			maxRatio = takerRatio;
			maxKey = `${p.nameKey}-taker`;
		}
		if (makerRatio > maxRatio) {
			maxRatio = makerRatio;
			maxKey = `${p.nameKey}-maker`;
		}
		return { product: p, takerRatio, makerRatio };
	});

	return { rows, maxRatio, maxKey };
}

/**
 * Login-gate simulator slider position (0–100) → friends' monthly traded
 * notional, log-scaled $10k…$10M and rounded to two significant digits so the
 * label reads like a round number ("$320K").
 */
export function simulatorVolume(t: number): number {
	const raw = 10 ** (4 + (3 * t) / 100);
	const magnitude = 10 ** Math.floor(Math.log10(raw) - 1);
	return Math.round(raw / magnitude) * magnitude;
}

/** Referrer's estimated monthly rebate for a given traded notional. */
export function estimateMonthlyRebate(
	volume: number,
	rebateRatePct: number,
): number {
	return (volume * rebateRatePct) / 100;
}

export const REFERRAL_PRODUCTS: ReferralProduct[] = [
	{
		nameKey: "referrals.fee.product-standard",
		descriptionKey: "referrals.fee.product-standard-desc",
		taker: 0.095,
		maker: 0.065,
		highlight: false,
	},
	{
		nameKey: "referrals.fee.product-low-fee",
		descriptionKey: "referrals.fee.product-low-fee-desc",
		taker: 0.0586,
		maker: 0.0529,
		highlight: true,
	},
];
